import { ApiError } from "./errors";
import { callGateway, type CallerIdentity } from "./gateway";
import { config } from "../config";

/**
 * Operator approval (radaccess /access/operator/{ns}/approval, superadmin-only
 * verdict). Approving runs radaccess's blocker check; from radaccess v1.217 it
 * also puts a signed-up operator on AnkhOS Launch with the standard trial
 * (owner 2026-10-30) and says what it did in `plan`, or why not in `planerror`.
 */
export interface ApprovalState {
  namespace: string;
  approved: boolean;
  blockers?: string[];
  plan?: string;
  planerror?: string;
}

const base = (ns: string) => `/operator/${encodeURIComponent(ns)}`;

export async function getApproval(identity: CallerIdentity, ns: string, signal?: AbortSignal): Promise<ApprovalState> {
  const r = await callGateway<ApprovalState>(identity, config().gatewayBase, `${base(ns)}/approval`, signal ? { signal } : {});
  return r.data;
}

/** Approve or withdraw. A refused approval (422) comes back as its blockers, not an error. */
export async function setApproval(identity: CallerIdentity, ns: string, approved: boolean): Promise<ApprovalState> {
  try {
    const r = await callGateway<ApprovalState>(identity, config().gatewayBase, `${base(ns)}/approval`, {
      method: "PUT",
      body: { approved },
    });
    return r.data;
  } catch (err) {
    if (err instanceof ApiError && err.status === 422 && err.body && typeof err.body === "object") {
      const b = err.body as Partial<ApprovalState>;
      return { namespace: ns, approved: false, blockers: Array.isArray(b.blockers) ? b.blockers : [] };
    }
    throw err;
  }
}

/** Superadmin's verdict that the PAN/GSTIN on record are genuine (radaccess taxinfo/verification). */
export async function setTaxVerified(identity: CallerIdentity, ns: string, verified: boolean): Promise<void> {
  await callGateway(identity, config().gatewayBase, `${base(ns)}/taxinfo/verification`, { method: "PUT", body: { verified } });
}

/** Superadmin's verdict after reading the licence document (radaccess license/verification). */
export async function setLicenseVerified(identity: CallerIdentity, ns: string, verified: boolean): Promise<void> {
  await callGateway(identity, config().gatewayBase, `${base(ns)}/license/verification`, { method: "PUT", body: { verified } });
}

/** Which "mark as checked" buttons a blocker list calls for (radaccess operatorapproval.go wording). */
export function verificationsNeeded(blockers: string[] | undefined): { tax: boolean; licence: boolean } {
  const b = blockers ?? [];
  return {
    tax: b.some((x) => x.startsWith("tax record is not verified")),
    licence: b.some((x) => x.startsWith("licence is claimed but not verified")),
  };
}
